"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { RefObject, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { InspirationCategoryDocument } from "@/prismicio-types";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";

type InspirationFilterProps = {
  locale: Locales;
  inspirationCategories: InspirationCategoryDocument[];
  currentCategoryUid: string;
};

const InspirationFilter = ({ locale, inspirationCategories, currentCategoryUid }: InspirationFilterProps) => {
  const pathname = usePathname();
  const dropdownRef = useRef<HTMLElement>(null);

  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [isNavigating, setIsNavigating] = useState(false);
  useOnClickOutside(dropdownRef as RefObject<HTMLElement>, () => setOpen(false));

  const buildUrl = (categoryUid: string) => {
    const segments = pathname.split("/");
    segments[segments.length - 1] = categoryUid;
    return segments.join("/");
  };

  return (
    <nav
      ref={dropdownRef}
      className="relative mx-auto -mt-5 w-[220px] md:hidden"
      aria-label={__("Inspiration category navigation", locale)}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={isNavigating}
        onClick={() => setOpen((prev) => !prev)}
        className="border-border-light flex w-full cursor-pointer items-center justify-between rounded-sm border bg-white p-2 px-4 text-sm font-semibold shadow-xs disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isNavigating ? (
          <span className="flex items-center gap-2 capitalize">
            <Loader2 className="size-4 animate-spin" />
            {inspirationCategories.find((cat) => cat.uid === selectedCategory)?.data.title || __("Loading...", locale)}
          </span>
        ) : (
          <span className="font-geist capitalize">{__("Filter by categories", locale)}</span>
        )}
        <ChevronDown size={20} className={cn("transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <ul className="border-border-light absolute z-50 mt-1 w-full rounded-sm border bg-white text-sm font-bold shadow-md">
          {inspirationCategories.map((category) => {
            const isActive = category.uid === currentCategoryUid;
            return (
              <li key={category.id}>
                <Link
                  href={buildUrl(category.uid)}
                  className={cn(
                    "font-geist block w-full px-4 py-2 text-left capitalize",
                    isActive ? "text-primary bg-gray-100" : "hover:bg-gray-50"
                  )}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => {
                    if (pathname === buildUrl(category.uid)) {
                      setOpen(false);
                      return;
                    }
                    setSelectedCategory(category.uid);
                    setIsNavigating(true);
                    setOpen(false);
                  }}
                >
                  {category.data.title}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
};

export default InspirationFilter;
